import { useState, useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { borclarData, type BorcRecord } from "@/mocks/borclar";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

const durumColors: Record<string, string> = {
  Bekliyor: "bg-gray-500/10 text-gray-400",
  Yaklaştı: "bg-gold-500/10 text-gold-400",
  Gecikti: "bg-red-500/10 text-red-400",
  Ödendi: "bg-emerald-500/10 text-emerald-400",
};

const turColors: Record<string, string> = {
  Altın: "bg-gold-500/10 text-gold-400",
  İşçilik: "bg-sky-500/10 text-sky-400",
  Taş: "bg-purple-500/10 text-purple-400",
  Ürün: "bg-emerald-500/10 text-emerald-400",
  Diğer: "bg-gray-500/10 text-gray-400",
};

const durumOptions = ["Tümü", "Bekliyor", "Yaklaştı", "Gecikti", "Ödendi"];
const turOptions = ["Tümü", "Altın", "İşçilik", "Taş", "Ürün", "Diğer"];

export default function Borclar() {
  const [search, setSearch] = useState("");
  const [durumFilter, setDurumFilter] = useState("Tümü");
  const [turFilter, setTurFilter] = useState("Tümü");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [data, setData] = useState<BorcRecord[]>(borclarData);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch =
        item.tedarikciAdi.toLowerCase().includes(search.toLowerCase()) ||
        item.not.toLowerCase().includes(search.toLowerCase());
      const matchesDurum = durumFilter === "Tümü" || item.durum === durumFilter;
      const matchesTur = turFilter === "Tümü" || item.borcTuru === turFilter;
      return matchesSearch && matchesDurum && matchesTur;
    });
  }, [data, search, durumFilter, turFilter]);

  const totalKalan = filtered.reduce((sum, item) => sum + item.kalan, 0);
  const totalOdenen = filtered.reduce((sum, item) => sum + item.odenen, 0);

  const handleAdd = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const tutar = Number(fd.get("tutar"));
    const odenen = Number(fd.get("odenen"));
    const yeni: BorcRecord = {
      id: Date.now(),
      tedarikciAdi: String(fd.get("tedarikciAdi") || ""),
      borcTuru: (fd.get("borcTuru") as BorcRecord["borcTuru"]) || "Diğer",
      tutar,
      odenen,
      kalan: tutar - odenen,
      vadeTarihi: String(fd.get("vadeTarihi") || ""),
      durum: (fd.get("durum") as BorcRecord["durum"]) || "Bekliyor",
      not: String(fd.get("not") || ""),
    };
    setData((prev) => [yeni, ...prev]);
    setShowAddForm(false);
    form.reset();
  };

  const handleEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingId) return;
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const tutar = Number(fd.get("tutar"));
    const odenen = Number(fd.get("odenen"));
    setData((prev) =>
      prev.map((item) =>
        item.id === editingId
          ? {
              ...item,
              tedarikciAdi: String(fd.get("tedarikciAdi") || ""),
              borcTuru: (fd.get("borcTuru") as BorcRecord["borcTuru"]) || item.borcTuru,
              tutar,
              odenen,
              kalan: tutar - odenen,
              vadeTarihi: String(fd.get("vadeTarihi") || ""),
              durum: (fd.get("durum") as BorcRecord["durum"]) || item.durum,
              not: String(fd.get("not") || ""),
            }
          : item
      )
    );
    setEditingId(null);
    form.reset();
  };

  const handleDelete = (id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
  };

  const editRecord = data.find((d) => d.id === editingId);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Borç (Verecek) Takibi</h1>
        <p className="text-sm text-gray-500">Tedarikçilere olan borçları takip edin ve yönetin.</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Borç (Görünen)</p>
          <p className="text-xl font-semibold text-red-400">{formatCurrency(filtered.reduce((s, i) => s + i.tutar, 0))}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Ödenen</p>
          <p className="text-xl font-semibold text-emerald-400">{formatCurrency(totalOdenen)}</p>
        </div>
        <div className="glass-card p-4">
          <p className="text-xs text-gray-500 mb-1">Toplam Kalan</p>
          <p className="text-xl font-semibold text-gold-400">{formatCurrency(totalKalan)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <i className="ri-search-line text-gray-500 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tedarikçi ara..."
              className="bg-transparent text-sm text-gray-300 placeholder-gray-500 outline-none w-48"
            />
          </div>
          <select value={turFilter} onChange={(e) => setTurFilter(e.target.value)} className="input-dark">
            {turOptions.map((t) => (
              <option key={t} value={t} className="bg-dark-950">{t}</option>
            ))}
          </select>
          <select value={durumFilter} onChange={(e) => setDurumFilter(e.target.value)} className="input-dark">
            {durumOptions.map((d) => (
              <option key={d} value={d} className="bg-dark-950">{d}</option>
            ))}
          </select>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-gold">
          <i className="ri-add-line" />
          Yeni Borç Ekle
        </button>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Tedarikçi</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Tür</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Tutar</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Ödenen</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">Kalan</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Vade</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Durum</th>
                <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">Not</th>
                <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} className="table-row-hover border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 text-sm text-gray-200 font-medium">{item.tedarikciAdi}</td>
                  <td className="px-4 py-3">
                    <span className={`status-badge ${turColors[item.borcTuru] || ""}`}>{item.borcTuru}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300 text-right">{formatCurrency(item.tutar)}</td>
                  <td className="px-4 py-3 text-sm text-emerald-400 text-right">{formatCurrency(item.odenen)}</td>
                  <td className="px-4 py-3 text-sm text-gold-400 text-right font-medium">{formatCurrency(item.kalan)}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.vadeTarihi}</td>
                  <td className="px-4 py-3">
                    <span className={`status-badge ${durumColors[item.durum] || ""}`}>{item.durum}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{item.not}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingId(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors cursor-pointer"
                        title="Düzenle"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors cursor-pointer"
                        title="Sil"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">Kayıt bulunamadı.</div>
        )}
      </div>

      {/* Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Yeni Borç Ekle</h3>
              <button onClick={() => setShowAddForm(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Tedarikçi Adı</label>
                <input name="tedarikciAdi" required className="input-dark w-full" placeholder="Tedarikçi adı" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Borç Türü</label>
                  <select name="borcTuru" className="input-dark w-full" defaultValue="Altın">
                    {turOptions.slice(1).map((t) => (
                      <option key={t} value={t} className="bg-dark-950">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Tutar</label>
                  <input name="tutar" type="number" required min={0} className="input-dark w-full" placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödenen</label>
                  <input name="odenen" type="number" required min={0} defaultValue={0} className="input-dark w-full" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Vade Tarihi</label>
                  <input name="vadeTarihi" type="date" required className="input-dark w-full" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                <select name="durum" className="input-dark w-full" defaultValue="Bekliyor">
                  <option value="Bekliyor" className="bg-dark-950">Bekliyor</option>
                  <option value="Yaklaştı" className="bg-dark-950">Yaklaştı</option>
                  <option value="Gecikti" className="bg-dark-950">Gecikti</option>
                  <option value="Ödendi" className="bg-dark-950">Ödendi</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={3} className="input-dark w-full resize-none" placeholder="Not ekle..." />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingId && editRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="glass-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-lg font-semibold text-gray-100">Borç Düzenle</h3>
              <button onClick={() => setEditingId(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-200 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Tedarikçi Adı</label>
                <input name="tedarikciAdi" defaultValue={editRecord.tedarikciAdi} required className="input-dark w-full" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Borç Türü</label>
                  <select name="borcTuru" className="input-dark w-full" defaultValue={editRecord.borcTuru}>
                    {turOptions.slice(1).map((t) => (
                      <option key={t} value={t} className="bg-dark-950">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Tutar</label>
                  <input name="tutar" type="number" defaultValue={editRecord.tutar} required min={0} className="input-dark w-full" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Ödenen</label>
                  <input name="odenen" type="number" defaultValue={editRecord.odenen} required min={0} className="input-dark w-full" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5">Vade Tarihi</label>
                  <input name="vadeTarihi" type="date" defaultValue={editRecord.vadeTarihi} required className="input-dark w-full" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Durum</label>
                <select name="durum" className="input-dark w-full" defaultValue={editRecord.durum}>
                  <option value="Bekliyor" className="bg-dark-950">Bekliyor</option>
                  <option value="Yaklaştı" className="bg-dark-950">Yaklaştı</option>
                  <option value="Gecikti" className="bg-dark-950">Gecikti</option>
                  <option value="Ödendi" className="bg-dark-950">Ödendi</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Not</label>
                <textarea name="not" rows={3} defaultValue={editRecord.not} className="input-dark w-full resize-none" />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingId(null)} className="btn-dark">İptal</button>
                <button type="submit" className="btn-gold">Güncelle</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
